import { type DatasourceResponseSchemaProperty } from '@atlaskit/linking-types';
import { type SerializedError } from '@databases/types';
import { type CloudId } from '@databases/types/confluence';

import { type DatasourceType } from '@canvas/atlassian-service/datasource';

import { type PayloadAction, createSlice } from '../../shims/redux-toolkit.ts';
import { type AsyncOperation, AsyncOperationStatus } from '../async-operation.ts';

export interface DatasourcesState {
  supportedProperties: Record<string, Record<string, DatasourceResponseSchemaProperty[]>>;
  supportedPropertiesStatus: Record<string, Record<string, AsyncOperation<void>>>;
}

type SupportedFieldsPayload = {
  cloudId: CloudId;
  datasourceType: DatasourceType;
  supportedProperties?: DatasourceResponseSchemaProperty[];
};

const initialState: DatasourcesState = {
  supportedProperties: {},
  supportedPropertiesStatus: {}
};

const datasourcesSlice = createSlice({
  name: 'datasources',
  initialState,
  reducers: {
    loadSupportedPropertiesPending: (
      state,
      action: PayloadAction<SupportedFieldsPayload>
    ) => {
      const { cloudId, datasourceType } = action.payload;
      const cloudIdKey = cloudId ?? '';

      if (!state.supportedPropertiesStatus[datasourceType]) {
        state.supportedPropertiesStatus[datasourceType] = {};
      }
      state.supportedPropertiesStatus[datasourceType][cloudIdKey] = {
        status: AsyncOperationStatus.PENDING
      };
    },

    loadSupportedPropertiesFinished: (
      state,
      action: PayloadAction<SupportedFieldsPayload>
    ) => {
      const { cloudId, datasourceType, supportedProperties } = action.payload;
      const cloudIdKey = cloudId ?? '';

      if (!state.supportedProperties[datasourceType]) {
        state.supportedProperties[datasourceType] = {};
      }
      if (!state.supportedPropertiesStatus[datasourceType]) {
        state.supportedPropertiesStatus[datasourceType] = {};
      }
      state.supportedProperties[datasourceType][cloudIdKey] = supportedProperties ?? [];
      state.supportedPropertiesStatus[datasourceType][cloudIdKey] = {
        status: AsyncOperationStatus.SUCCEEDED
      };
    },

    loadSupportedPropertiesError: (
      state,
      action: PayloadAction<SupportedFieldsPayload & { error: SerializedError }>
    ) => {
      const { cloudId, datasourceType, error } = action.payload;
      const cloudIdKey = cloudId ?? '';

      if (!state.supportedPropertiesStatus[datasourceType]) {
        state.supportedPropertiesStatus[datasourceType] = {};
      }
      state.supportedPropertiesStatus[datasourceType][cloudIdKey] = {
        status: AsyncOperationStatus.ERROR,
        error
      };
    }
  }
});

export default datasourcesSlice;
